import {
  __,
  allPass,
  binary,
  bind,
  call,
  compose,
  concat,
  converge,
  curry,
  curryN,
  F,
  find,
  identity,
  ifElse,
  length,
  lt,
  nthArg,
  objOf,
  path,
  pathEq,
  pathOr,
  pathSatisfies,
  pick,
  pipe,
  prop,
  propEq,
  T,
  toLower,
  useWith,
} from 'ramda'
import {
  SET_WORD,
  SHARE_WORD_COPY,
  SHARE_WORD_DONE,
  SHARE_WORD_START,
} from '../reducers/createGame'
import {PLAY, ROOT} from '../routes'
import {selectIsComplete, selectIsLost} from '../selectors/gameState'

const DUMMY_WARNING = 'Could not initialise firebase analytics, logging ' +
  'events to console instead.'

const analytics = getFirebaseAnalytics()

export const logEvent = curry(binary(bind(analytics.logEvent, analytics)))

const createHandler = useWith(curry(ifElse)(__, __, F), [
  identity,
  curryN(2, compose)(T),
])

const currentLocation = path(['meta', 'location', 'current'])

const isWordPresent = pipe(
  nthArg(1),
  pathSatisfies(pipe(length, lt(0)), ['createGame', 'word']),
)

const isTypeEq = propEq('type')

const createHandlerForCreateGame = type => createHandler(
  allPass([isTypeEq(type), isWordPresent]),
  converge(logEvent, [
    pipe(prop('type'), toLower, concat('create_')),
    pipe(nthArg(1), prop('createGame'), pick(['word'])),
  ]),
)

const selectQuery = path(['meta', 'location', 'current', 'query'])
const selectLastQuery = path(['meta', 'location', 'prev', 'query'])

const isPlayType = isTypeEq(PLAY)

const isPlayAction = allPass([
  isPlayType,
  pathEq(['meta', 'location', 'kind'], 'redirect'),
])

const handlers = [
  createHandler(
    pathEq(['meta', 'location', 'kind'], 'load'),
    pipe(currentLocation, pick(['pathname', 'type']), logEvent('load')),
  ),
  createHandlerForCreateGame(SET_WORD),
  createHandlerForCreateGame(SHARE_WORD_START),
  createHandlerForCreateGame(SHARE_WORD_DONE),
  createHandlerForCreateGame(SHARE_WORD_COPY),
  createHandler(
    allPass([isPlayAction, pipe(nthArg(1), selectIsComplete)]),
    pipe(selectQuery, logEvent('play_complete')),
  ),
  createHandler(
    allPass([isPlayAction, pipe(nthArg(1), selectIsLost)]),
    pipe(selectQuery, logEvent('play_lost')),
  ),
  createHandler(
    isPlayAction,
    pipe(selectQuery, logEvent('play_try')),
  ),
  createHandler(
    allPass([pathEq(['meta', 'location', 'kind'], 'push'), isTypeEq(ROOT)]),
    pipe(selectLastQuery, logEvent('play_share')),
  ),
]

const middleware = store => next => action => {
  const result = next(action)
  logActionEvent(action, store.getState())
  return result
}

function logActionEvent(action, state) {
  return find((fn) => fn(action, state), handlers)
}

function getFirebaseAnalytics() {
  return call(pathOr(dummyService, ['firebase', 'analytics'], window))
}

function dummyService() {
  console.warn(DUMMY_WARNING)
  return objOf(
    'logEvent',
    (...args) => console.info('Analytics event: ', ...args),
  )
}

export default middleware